import {
    keepPreviousData,
    QueryFunction,
    QueryStatus,
    useMutation,
    useQueries,
    useQuery,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import {
    ClientInferResponseBody,
    ServerInferResponseBody,
} from "@ts-rest/core";
import {
    billContract,
    BillFilters,
    BillQueryParams,
    BillStatsParams,
    BillsByCounterpartyQueryParams,
} from "@common/contracts/bill.contract";
import { chunk } from "@common/helpers/collections";
import { withCanonicalDateFilters } from "@common/helpers/date";
import { PatchImporterBillItem } from "@common/schemas/domains/bills/patch-importer-bills.schema";
import { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";
import { BillSource, BillStatus } from "@common/types/bill";
import { ResourceType } from "@common/types/auditLog";
import {
    AccrualMatchedTransactionDisplay,
    BillAccrualMatchSummary,
    MAX_DOCUMENT_ACCRUAL_MATCH_SUMMARY_IDS,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { ImportedSettlementDisplay } from "@common/schemas/domains/accrualReview/imported-settlement-display.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";
import { auditLogQueryKeys } from "./auditLog.api";
import { invalidateHasMatchableDocumentsQuery } from "./accrual-review.api";

export const BILL_ACCRUAL_MATCHES_QUERY_KEY = "bill-accrual-matches-v2";
export const BILL_IMPORTED_SETTLEMENTS_QUERY_KEY = "bill-imported-settlements";

const billContractClient = getBackendAPIClient(billContract, undefined, {
    jsonQuery: true,
});

type BillQueryKey = ["bills", string | undefined, BillQueryParams | undefined];
type BillBodyQueryKey = [...BillQueryKey, "body"];
type BillStatsQueryKey = [
    "billStats",
    string | undefined,
    BillStatsParams | undefined,
];

type BillsByCounterpartyQueryKey = [
    "billsByCounterparty",
    string | undefined,
    BillsByCounterpartyQueryParams | undefined,
];

type GetBillsResponse = ClientInferResponseBody<
    typeof billContract.getBills,
    200
>;
type GetBillStatsResponse = ServerInferResponseBody<
    typeof billContract.getBillStats,
    200
>;
type GetBillsByCounterpartyResponse = ServerInferResponseBody<
    typeof billContract.getBillsByCounterparty,
    200
>;

const getBillsQueryKey = (
    workspaceId?: string,
    params?: BillQueryParams,
): BillQueryKey => ["bills", workspaceId, params];

const getBillStatsQueryKey = (
    workspaceId?: string,
    params?: BillStatsParams,
): BillStatsQueryKey => ["billStats", workspaceId, params];

const getBillsByCounterpartyQueryKey = (
    workspaceId?: string,
    params?: BillsByCounterpartyQueryParams,
): BillsByCounterpartyQueryKey => ["billsByCounterparty", workspaceId, params];

export function useBills(
    query: BillQueryParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const { workspaceId } = useScope();
    const queryKey = getBillsQueryKey(workspaceId ?? undefined, query);

    return useQuery({
        queryKey,
        queryFn: fetchBills,
        enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

const toBillsRequestParams = (
    query: BillQueryParams | undefined,
): BillQueryParams => ({
    ...query,
    filters: query?.filters
        ? withCanonicalDateFilters(query.filters)
        : query?.filters,
    page: (query?.page ?? 1) - 1,
    limit: query?.limit ?? 10,
});

export const getBillsBodyQueryKey = (
    workspaceId: string | undefined,
    query: BillQueryParams,
): BillBodyQueryKey => [...getBillsQueryKey(workspaceId, query), "body"];

/** One page of bills (`page` is 1-based), with the filters in the request body. */
export async function loadBillsBody(
    workspaceId: string,
    query: BillQueryParams,
) {
    const result = await billContractClient.queryBills({
        params: { workspaceId },
        body: toBillsRequestParams(query),
    });
    if (result.status !== 200) {
        throw new Error("Failed to load bills");
    }
    return result.body;
}

const fetchBillsQuery: QueryFunction<
    GetBillsResponse | undefined,
    BillBodyQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId || !query) {
        return;
    }
    return await loadBillsBody(workspaceId, query);
};

/**
 * Bills page like `useBills`, with the filters sent in the request body so a
 * task's pinned bill ids do not have to fit in a URL. Cached under the "bills"
 * key so bill mutations refresh it.
 */
export function useBillsQuery(
    query: BillQueryParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const { workspaceId } = useScope();
    const queryKey = getBillsBodyQueryKey(workspaceId ?? undefined, query);

    return useQuery({
        queryKey,
        queryFn: fetchBillsQuery,
        enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

// The workspace-records probe only checks whether any records exist at all,
// which changes rarely; a short stale window keeps it from re-firing (and
// flashing the empty state) as the user edits search/filters.
const WORKSPACE_RECORDS_PROBE_STALE_TIME = 60_000;

export function useHasBillsInWorkspace(enabled: boolean) {
    const { workspaceId } = useScope();

    const { data, isLoading } = useQuery({
        queryKey: getBillsQueryKey(workspaceId ?? undefined, {
            page: 1,
            limit: 1,
        }),
        queryFn: fetchBills,
        enabled,
        staleTime: WORKSPACE_RECORDS_PROBE_STALE_TIME,
    });

    return {
        hasRecords: (data?.total ?? 0) > 0,
        isChecking: enabled && isLoading,
    };
}

export function useHasBillCounterpartiesInWorkspace(enabled: boolean) {
    const { workspaceId } = useScope();

    const { data, isLoading } = useQuery({
        queryKey: getBillsByCounterpartyQueryKey(workspaceId ?? undefined, {
            page: 1,
            limit: 1,
        }),
        queryFn: fetchBillsByCounterparty,
        enabled,
        staleTime: WORKSPACE_RECORDS_PROBE_STALE_TIME,
    });

    return {
        hasRecords: (data?.total ?? 0) > 0,
        isChecking: enabled && isLoading,
    };
}

type LastBillNumberQueryKey = ["lastBillNumber", string | undefined];

const getLastBillNumberQueryKey = (
    workspaceId?: string,
): LastBillNumberQueryKey => ["lastBillNumber", workspaceId];

const fetchLastBillNumber: QueryFunction<
    string | null,
    LastBillNumberQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId] = queryKey;
    if (!workspaceId) {
        return null;
    }
    const result = await billContractClient.getLastBillNumber({
        params: { workspaceId },
    });
    return result.body.invoiceNumber;
};

export function useLastBillNumber({
    enabled = true,
}: { enabled?: boolean } = {}) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: getLastBillNumberQueryKey(workspaceId ?? undefined),
        queryFn: fetchLastBillNumber,
        enabled: enabled && Boolean(workspaceId),
        staleTime: 0,
    });
}

export function useBillStats(query: BillStatsParams) {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: getBillStatsQueryKey(workspaceId ?? undefined, query),
        queryFn: fetchBillStats,
    });
}

export function useBillsByCounterparty(
    query: BillsByCounterpartyQueryParams,
    { enabled = true }: { enabled?: boolean } = {},
) {
    const { workspaceId } = useScope();
    const queryKey = getBillsByCounterpartyQueryKey(
        workspaceId ?? undefined,
        query,
    );

    return useQuery({
        queryKey,
        queryFn: fetchBillsByCounterparty,
        enabled,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

export function useCreateBills() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            bills,
        }: {
            workspaceId: string;
            bills: Parameters<
                typeof billContractClient.createBills
            >[0]["body"]["bills"];
        }) => {
            const result = await billContractClient.createBills({
                params: { workspaceId },
                body: { bills },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

export function usePatchImporterBills() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            idsToRemove,
            upserts,
        }: {
            workspaceId: string;
            idsToRemove: string[];
            upserts: PatchImporterBillItem[];
        }) => {
            const result = await billContractClient.patchImporterBills({
                params: { workspaceId },
                body: { idsToRemove, upserts },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

/** Resolves once the bill lists have refetched; the other queries refresh in the background. */
export function invalidateBillQueries(): Promise<void> {
    const billListsRefetched = queryClient.invalidateQueries({
        queryKey: ["bills"],
    });
    queryClient.invalidateQueries({
        queryKey: ["billStats"],
    });
    queryClient.invalidateQueries({
        queryKey: ["billsByCounterparty"],
    });
    queryClient.invalidateQueries({
        queryKey: ["lastBillNumber"],
    });
    invalidateHasMatchableDocumentsQuery();
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey;
            if (key[0] !== auditLogQueryKeys.all[0]) {
                return false;
            }
            if (key[1] === "byResource") {
                return key[3] === ResourceType.BILL;
            }
            return key[1] === "history" || key[1] === "history-details";
        },
    });
    return billListsRefetched;
}

export function findBillInListCache(billId: string) {
    const cachedQueries = queryClient.getQueriesData<GetBillsResponse>({
        queryKey: ["bills"],
        type: "active",
    });
    for (const [, response] of cachedQueries) {
        const match = response?.data.find((bill) => bill.id === billId);
        if (match) {
            return match;
        }
    }
    return undefined;
}

export async function loadBillAfterRefresh({
    workspaceId,
    billId,
}: {
    workspaceId: string;
    billId: string;
}) {
    const cachedBill = findBillInListCache(billId);
    if (cachedBill) {
        return cachedBill;
    }
    const page = await loadBillsBody(workspaceId, {
        filters: { ids: [billId] },
        page: 1,
        limit: 1,
    });
    return page.data[0];
}

const fetchBills: QueryFunction<
    GetBillsResponse | undefined,
    BillQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await billContractClient.getBills({
        params: { workspaceId },
        query: toBillsRequestParams(query),
    });
    return result.body;
};

const fetchBillStats: QueryFunction<
    GetBillStatsResponse | undefined,
    BillStatsQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await billContractClient.getBillStats({
        params: { workspaceId },
        query: {
            ...query,
            filters: query?.filters
                ? withCanonicalDateFilters(query.filters)
                : query?.filters,
        },
    });
    return result.body;
};

const fetchBillsByCounterparty: QueryFunction<
    GetBillsByCounterpartyResponse | undefined,
    BillsByCounterpartyQueryKey
> = async ({ queryKey }) => {
    const [, workspaceId, query] = queryKey;

    if (!workspaceId) {
        return;
    }
    const result = await billContractClient.getBillsByCounterparty({
        params: { workspaceId },
        query: {
            ...query,
            filters: query?.filters
                ? withCanonicalDateFilters(query.filters)
                : query?.filters,
            page: (query?.page ?? 1) - 1,
            limit: query?.limit ?? 10,
        },
    });
    return result.body;
};

export function useUpdateBillLineItem() {
    return useMutation({
        mutationFn: ({
            workspaceId,
            lineItemId,
            classIds,
            accountOverrides,
            accountsPayableOverrides,
            accountId,
        }: {
            workspaceId: string;
            lineItemId: string;
            classIds?: string[];
            accountOverrides?: AccountOverrideEntryRequest[];
            accountsPayableOverrides?: AccountOverrideEntryRequest[];
            accountId?: string | null;
        }) =>
            updateBillLineItem({
                workspaceId,
                lineItemId,
                classIds,
                accountOverrides,
                accountsPayableOverrides,
                accountId,
            }),
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

export function useBulkUpdateBillLineItems() {
    return useMutation({
        mutationFn: ({
            workspaceId,
            classIds,
            accountOverrides,
            accountsPayableOverrides,
            lineItemIds,
            filters,
        }: {
            workspaceId: string;
            classIds?: string[];
            accountOverrides?: AccountOverrideEntryRequest[];
            accountsPayableOverrides?: AccountOverrideEntryRequest[];
            lineItemIds?: string[];
            filters?: BillFilters;
        }) =>
            bulkUpdateBillLineItems({
                workspaceId,
                classIds,
                accountOverrides,
                accountsPayableOverrides,
                lineItemIds,
                filters,
            }),
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

async function updateBillLineItem({
    workspaceId,
    lineItemId,
    classIds,
    accountOverrides,
    accountsPayableOverrides,
    accountId,
}: {
    workspaceId: string;
    lineItemId: string;
    classIds?: string[];
    accountOverrides?: AccountOverrideEntryRequest[];
    accountsPayableOverrides?: AccountOverrideEntryRequest[];
    accountId?: string | null;
}) {
    const result = await billContractClient.updateBillLineItem({
        params: { workspaceId, lineItemId },
        body: {
            classIds,
            accountOverrides,
            accountsPayableOverrides,
            accountId,
        },
    });

    return result.body;
}

export function useUpdateBill() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            billId,
            paidAt,
            ...body
        }: {
            workspaceId: string;
            billId: string;
            memo?: string | null;
            status?: BillStatus;
            paidAt?: string | null;
        }) => {
            const result = await billContractClient.updateBill({
                params: { workspaceId, billId },
                body: {
                    ...body,
                    ...(paidAt !== undefined && {
                        paidAt: paidAt ? new Date(paidAt) : null,
                    }),
                },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
        onError: () => {
            invalidateBillQueries();
        },
    });
}

export function useLinkDocumentsToBill() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            billId,
            financialDocumentIds,
        }: {
            workspaceId: string;
            billId: string;
            financialDocumentIds: number[];
        }) => {
            const result = await billContractClient.linkDocumentToBill({
                params: { workspaceId, billId },
                body: { financialDocumentIds },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

export function useUnlinkDocumentsFromBill() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            billId,
            financialDocumentIds,
        }: {
            workspaceId: string;
            billId: string;
            financialDocumentIds: number[];
        }) => {
            const result = await billContractClient.unlinkDocumentFromBill({
                params: { workspaceId, billId },
                body: { financialDocumentIds },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

export function useDeleteBill() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            billId,
        }: {
            workspaceId: string;
            billId: string;
        }) => {
            const result = await billContractClient.deleteBill({
                params: { workspaceId, billId },
            });
            return result.body;
        },
        onSuccess: () => {
            invalidateBillQueries();
        },
    });
}

export async function getBillAccrualMatches({
    workspaceId,
    billId,
}: {
    workspaceId: string;
    billId: string;
}): Promise<AccrualMatchedTransactionDisplay[]> {
    const result = await billContractClient.getBillAccrualMatches({
        params: { workspaceId, billId },
    });
    if (result.status !== 200) {
        return [];
    }
    return result.body.matches;
}

async function getBillAccrualMatchSummaries({
    workspaceId,
    billIds,
}: {
    workspaceId: string;
    billIds: string[];
}): Promise<BillAccrualMatchSummary[]> {
    const result = await billContractClient.getBillAccrualMatchSummaries({
        params: { workspaceId },
        query: { billIds },
    });
    if (result.status !== 200) {
        throw new Error("Failed to load bill matches");
    }
    return result.body.summaries;
}

type AccrualMatchSummaryResults = Array<{
    status: QueryStatus;
    data: BillAccrualMatchSummary[] | undefined;
}>;

function resolveBatchStatus(results: AccrualMatchSummaryResults): QueryStatus {
    if (results.some((result) => result.status === "error")) {
        return "error";
    }
    if (
        results.length === 0 ||
        results.some((result) => result.status === "pending")
    ) {
        return "pending";
    }
    return "success";
}

function combineAccrualMatchSummaryBatches(
    results: AccrualMatchSummaryResults,
) {
    const status = resolveBatchStatus(results);
    return {
        status,
        data:
            status === "success"
                ? results.flatMap((result) => result.data ?? [])
                : undefined,
    };
}

export function useBillAccrualMatchSummaries({
    billIds,
    enabled,
}: {
    billIds: string[];
    enabled: boolean;
}) {
    const { workspaceId } = useScope();
    const batches = useMemo(
        () => chunk(billIds, MAX_DOCUMENT_ACCRUAL_MATCH_SUMMARY_IDS),
        [billIds],
    );
    return useQueries({
        queries: batches.map((batch) => ({
            queryKey: [
                BILL_ACCRUAL_MATCHES_QUERY_KEY,
                workspaceId,
                "summaries",
                batch,
            ],
            queryFn: () =>
                getBillAccrualMatchSummaries({
                    workspaceId: workspaceId!,
                    billIds: batch,
                }),
            enabled: enabled && Boolean(workspaceId),
            placeholderData: keepPreviousData,
        })),
        combine: combineAccrualMatchSummaryBatches,
    });
}

export function useBillAccrualMatches(billId: string | undefined) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: [BILL_ACCRUAL_MATCHES_QUERY_KEY, workspaceId, billId],
        queryFn: () =>
            getBillAccrualMatches({
                workspaceId: workspaceId!,
                billId: billId!,
            }),
        enabled: Boolean(workspaceId && billId),
    });
}

async function getBillImportedSettlements({
    workspaceId,
    billId,
}: {
    workspaceId: string;
    billId: string;
}): Promise<ImportedSettlementDisplay[]> {
    const result = await billContractClient.getBillImportedSettlements({
        params: { workspaceId, billId },
    });
    if (result.status !== 200) {
        return [];
    }
    return result.body.settlements;
}

export function useBillImportedSettlements({
    billId,
    source,
}: {
    billId: string | undefined;
    source: BillSource | undefined;
}) {
    const { workspaceId } = useScope();
    return useQuery({
        queryKey: [BILL_IMPORTED_SETTLEMENTS_QUERY_KEY, workspaceId, billId],
        queryFn: () =>
            getBillImportedSettlements({
                workspaceId: workspaceId!,
                billId: billId!,
            }),
        enabled: Boolean(
            workspaceId && billId && source === BillSource.QUICKBOOKS,
        ),
    });
}

async function bulkUpdateBillLineItems({
    workspaceId,
    classIds,
    accountOverrides,
    accountsPayableOverrides,
    lineItemIds,
    filters,
}: {
    workspaceId: string;
    classIds?: string[];
    accountOverrides?: AccountOverrideEntryRequest[];
    accountsPayableOverrides?: AccountOverrideEntryRequest[];
    lineItemIds?: string[];
    filters?: BillFilters;
}) {
    const result = await billContractClient.bulkUpdateBillLineItems({
        params: { workspaceId },
        body: {
            classIds,
            accountOverrides,
            accountsPayableOverrides,
            lineItemIds,
            filters,
        },
    });

    return result.body;
}
